<template>
    <div class="save-reminder">
        <img style="margin-top: 23px" src="~@/assets/images/logos/vase.svg" alt="vase">

        <div class="save-reminder--heading">
            <h1 class="save-reminder--title">{{$t('GIFTING_REMINDER_PREPARE_GIFT')}}</h1>
            <p class="save-reminder--paragraph">{{$t('GIFTING_REMINDER_DELIVER_TO', { first: isSubscription ? 'first' : '', firstName: address.first_name})}}</p>
        </div>

        <div class="save-reminder--banner">
            <h2 style="font-size: 20px; line-height: 25px;">🎉 {{firstDeliveryDate}}</h2>
        </div>

        <div v-if="!getSelectedOccasion && specialDatesEnabled" class="add-date">
            <img v-if="hasSaved" class="image" style="margin-top: 23px" src="~@/assets/images/logos/vectro.svg">
            <h2 v-if="hasSaved" class="add-date--heading" v-text="$t('GIFTING_REMINDER_SAVED')"></h2>
            <h2 v-else class="add-date--heading" v-text="$t('SAVE_THIS_DATE')"></h2>
            <p class="add-date--sub-heading" v-text="$t('ADD_DATE_TEXT_DESCRIPTION')"></p>
            <p v-if="!hasSaved" class="add-date--questions" v-html="$t('SPECIAL_DATES_EVENT_NAME')"></p>
            <input v-if="!hasSaved"
                class="input__field add-date--input"
                placeholder="e.g, Birthday, Anniversary etc."
                v-model="addDate.name"/>
            <p class="input-error" v-if="errorHandler.response && errorHandler.response.name">{{errorHandler.response.name[0]}}</p>
            <p v-if="!hasSaved" class="add-date--questions" v-html="$t('SPECIAL_DATES_SELECT_DATE')"></p>
            <div v-if="!hasSaved" class="date-select">
                <multiselect
                    class="date-select--day"
                    v-model="addDate.day"
                    :options="dayOptions"
                    :show-labels="false"
                    :allow-empty="false"
                    :close-on-select="true"
                    placeholder="Day"></multiselect>
                <multiselect
                    class="date-select--month"
                    v-model="addDate.month"
                    :options="months"
                    :show-labels="false"
                    :allow-empty="false"
                    :close-on-select="true"
                    placeholder="Month"></multiselect>
            </div>
            <p class="input-error" v-if="errorHandler.response && errorHandler.response.date">{{errorHandler.response.date[0]}}</p>
        </div>
        <button
            v-if="!getSelectedOccasion && !hasSaved && specialDatesEnabled"
            slot="actions-suffix"
            :disabled="isProcessing"
            @click="onAddDay"
            class="btn btn--green add-date-btn--add">{{getSubmitButtonText}}</button>

        <button
            v-if="hasSaved && showAddDateButton"
            slot="actions-suffix"
            @click="$router.push({ name: 'account.gifting-reminders' })"
            class="btn btn--green add-date-btn--add">{{$t('GIFTING_REDIRECT')}}</button>

        <div class="save-reminder--table-container">
            <div class="gift-details">
                <h6>{{$t('GIFTING_GIFT_DETAILS')}}</h6>
                <p class="font-light">{{ summaryString }}</p>
                <p class="font-light" v-html="$t('THANK_YOU_ORDER_NUMBER', { giftReference })"></p>
            </div>
            <div class="gift-details">
                <h6>{{$t('GIFTING_GIFT_RECIPIENT')}}</h6>
                <p>{{`${address.first_name} ${address.last_name}`}}</p>
                <p>{{address.address1}}</p>
                <p>{{address.address2}}</p>
                <p>{{address.city}}</p>
                <p>{{address.postcode}}</p>
                <p>{{$t('THANK_YOU_IF_YOU_LIKE_SOMETHING', { name: address.first_name })}}
                    <a class="link" target="_blank" :href="giftNoteUrl"
                        v-text="$t('THANK_YOU_DOWNLOAD_IT_HERE')"></a></p>
            </div>
            <div class="gift-details">
                <h6>{{$t('GIFTING_GIFT_PAYMENT_METHOD')}}</h6>
                <p>{{$t('GIFTING_GIFT_CARD_ENDING_WITH', {cardEnding: this.cardEnding})}}</p>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import { formatDate } from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';

export default {
    name: 'SaveGiftingReminder',
    components: {
        Multiselect,
    },
    data() {
        return {
            errorHandler: new ErrorResponseHandler(),
            isProcessing: false,
            addDate: {
                name: '',
                day: '',
                month: '',
            },
            hasSaved: false,
        };
    },
    mounted() {
        const selectedDate = this.$store.getters['gifting/getDeliveryDateSelected'];
        if (selectedDate) {
            this.addDate.day = this.getDay;
            this.addDate.month = this.getMonth;
        }
    },
    computed: {
        ...mapGetters({
            selectedPlan: 'gifting/getFrequencyForSubscription',
            totalDeliveries: 'gifting/getNumberOfDeliveriesForSubscription',
            getSelectedOccasion: 'specialDates/getSelectedOccasion',
            isSubscription: 'gifting/isSubscription',
            numberOfMonths: 'gifting/getNumberOfMonthsForSubscription',
            frequency: 'gifting/getFrequencyForSubscription',
            giftNoteUrl: 'gifting/getGiftNoteUrl',
            giftReference: 'gifting/getGiftReference',
            specialDatesEnabled: 'account/getSpecialDatesEnabled',
            address: 'address/getSelectedGiftingAddress',
            cardEnding: 'payment/getCardEnding',
            months: 'specialDates/getMonthOptions',
            specialDatesCouponData: 'account/getSpecialDatesCoupon',
            products: 'gifting/getGiftProducts',
            getBoxName: 'gifting/getOrderedBoxName',
        }),
        date() {
            const selectedDate = this.$store.getters['gifting/getDeliveryDateSelected'];
            if (selectedDate) return new Date(selectedDate);
            return new Date();
        },
        getDay() {
            return this.date.getDate();
        },
        showAddDateButton() {
            if (this.specialDatesCouponData.special_dates_gifting_coupon_redeemed) return false;
            return Number(this.specialDatesCouponData.special_dates_coupon_min_dates) - Number(this.specialDatesCouponData.special_dates_count);
        },
        getMonth() {
            return this.months[this.date.getMonth()];
        },
        dayOptions() {
            return Array(31).fill().map((_, i) => (i + 1).toString());
        },
        getSubmitButtonText() {
            if (this.isProcessing) return this.$t('SPECIAL_DATES_WAIT');
            else return this.$t('SPECIAL_DATES_UPDATE');
        },
        firstDeliveryDate() {
            const date = this.$store.getters['gifting/getDeliveryDateSelected'];
            return formatDate(date, Dates.DAY_DATE_MONTH);
        },

        summaryString() {
            if (!this.isSubscription) {
                return this.$t('PAYMENT_SUMMARY_ONE_OFF_BOX', { boxType: this.getBoxName });
            }

            const monthString = this.numberOfMonths === 1 ? this.$t('PAYMENT_SUMMARY_MONTH') : this.$t('PAYMENT_SUMMARY_MONTHS');
            const frequencyString = this.frequency.toLowerCase();

            return this.$t('PAYMENT_SUMMARY_OF_DELIVERIES', {
                'numberOfMonths': this.$t(`${this.numberOfMonths}`),
                'monthString': this.$t(`${monthString}`),
                'frequencyString': this.$t(`${frequencyString}`),
            });
        },
    },
    methods: {
        close() {
            this.$emit('closed');
        },
        onAddDay() {
            this.isProcessing = true;
            this.addDate.date = `${this.months.indexOf(this.addDate.month) + 1}-${this.addDate.day}`;
            let response = null;
            this.errorHandler.setResponse(null);
            response = this.$store.dispatch('specialDates/addSpecialDate', this.addDate);
            response.then((e) => {
                this.isProcessing = false;
                if (e.errors) this.errorHandler.setResponse(e.errors[0]);
                else {
                    this.hasSaved = true;
                    this.$store.dispatch('account/fetchUserData', true);
                    this.close();
                };
            });
        },
    },
};
</script>

<style lang="scss" scoped>
    .save-reminder {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        padding: 24px;
        min-width: 375px;

        &--heading {
            max-width: 308px;
        }

        &--title {
            margin-top: 38px;
            font-size: 30px;
            line-height: 39px;
            font-weight: 400;
            text-align: center;
        }

        &--paragraph {
            font-size: 16px;
            font-weight: 300;
            line-height: 22.5px;
            text-align: center;
        }

        &--banner {
            display: flex;
            justify-content: space-around;
            align-items: center;
            padding: 16px;
            min-width: 308px;
            background: #F4F8F0;
            border-radius: 3px;

            &--img {
                height: 20px;
                width: 12px;
                left: 6px;
                top: 2px;
                border-radius: 0px;
            }
            &--text {
                font-size: 14px;
                line-height: 21px;
                font-weight: 400;
            }
        }

        &--table-container {
            border: 1px solid #DFDFDF;
            min-width: 327px;
            margin-top: 20px;
        }

        &--button-container {
            display: flex;
            flex-direction: column;
            margin-top: 56px;
        }

        &--button {
            min-width: 308px;
        }

        &--disabled-button {
            min-width: 308px;
            background: #F7F7F7;
            color: #CECECE;
        }

        @media (min-width: 1024px){
            .save-reminder {
                min-width: 540px;
                &--heading {
                    min-width: 540px;
                }

                &--title {
                    font-size: 36px;
                    line-height: 46px;
                    font-weight: 400;
                }

                &--paragraph {
                    font-size: 20px;
                    font-weight: 300;
                    line-height: 25px;
                    text-align: center;
                }

                &--banner {
                    min-width: 540px;
                    margin-top: 40px;
                    &--img {
                        width: 40px;
                        height: 54px;
                        margin-left: 40px;
                    }
                    &--text {
                        margin-left: 20px;
                    }
                }

                &--table-container {
                    border: 1px solid #DFDFDF;
                    min-width: 540px;
                    margin-top: 20px;
                }
            }

            &--button-container {
                flex-direction: row;
                align-items: center;
                margin-top: 64px;
            }

            &--button {
                min-width: 352px;
            }

            &--disabled-button {
                margin-left: 24px;
                min-width: 352px;
            }
        }

        @media (min-width: 768px){
            .save-reminder {
                min-width: 540px;
                &--table-container {
                    border: 1px solid #DFDFDF;
                    width: 540px;
                    margin-top: 20px;
                }
            }

            &--heading {
                min-width: 540px;
            }

            &--title {
                font-size: 36px;
                line-height: 46px;
                font-weight: 400;
                text-align: center;
            }

            &--paragraph {
                font-size: 20px;
                font-weight: 300;
                line-height: 25px;
                text-align: center;
            }

            &--banner {
                min-width: 540px;
                margin-top: 40px;
                margin-bottom: 64px;
                &--img {
                    width: 40px;
                    height: 54px;
                    margin-left: 40px;
                }
                &--text {
                    margin-left: 20px;
                }
            }

            &--grid-container{
                max-width: 752px;
            }

            &--grid-layout{
                display: grid;
                gap: 1rem;
                grid-template-columns: 352px 352px;
            }

            &--button-container {
                flex-direction: row;
                align-items: center;
                margin-top: 64px;
            }

            &--button {
                min-width: 352px;
            }

            &--disabled-button {
                margin-left: 24px;
                min-width: 352px;
            }
        }

        @media (max-width: 930px){
            &--disabled-button {
                margin-top: 16px;
            }
        }
    }

    .gift-details {
        border: 0.5px solid #DFDFDF;
        padding: 10px;
    }

    .add-date {
        width: 327px;
        margin: 20px auto;
        display: flex;
        flex-direction: column;
        &--heading {
            font-size: 25px;
            font-weight: 400;
            line-height: 30px;
            letter-spacing: 0em;
            text-align: left;
            margin-top: 30px;
        }

        &--sub-heading {
            font-size: 16px;
            font-weight: 300;
            line-height: 22px;
            letter-spacing: -0.011em;
            text-align: left;
        }

        &--questions {
            font-size: 14px;
            font-weight: 400;
            line-height: 21px;
            letter-spacing: 0em;
            text-align: left;
            margin-top: 24px;
        }

        &-btn {
            &--cancel, &--add {
                width: 327px;
                height: 55px;
                margin: 10px 10px;
            }
        }
        &--input {
            width: 100%;
            border: 1px solid #707070;
            padding: 10px 20px;
            color: #1a1a1a;
            font-weight: 200;
            &::placeholder {
                color: #1a1a1a;
            }
        }
        &--recurring {
            margin-top: 20px;
            padding: 0;
        }
    }

.date-select {
    display: flex;
    &--day, &--month, &--year {
        height: 60px;
        width: 110px;
        left: 0px;
        top: 0px;
        border-radius: 2px;
        @media (max-width: 640px) {
            margin: 0 5px;
        }
    }
    &--day{
        margin-left: 0;
    }
    &--month {
        width: 240px;
        margin: 0px;
        margin-left: 20px;
    }
}
.input-error {
    line-height: 15px;
    display: block;
    color: #b1473c;
    margin-bottom: 1.5rem;
    margin-top: 0.75rem;
    width: 100%;
    font-weight: 200;
    font-size: .75rem;
}

@media (min-width: 1024px){
        .add-date {
            min-width: 540px;
            &-btn {
                &--cancel, &--add {
                    width: 540px;
                    height: 55px;
                    margin: 10px 10px;
                }
            }
        }

        .date-select {
            &--month {
                width: 408px;
                margin: 0px;
                margin-left: 20px;
            }
        }
    }

    @media (min-width: 768px){
        .add-date {
            min-width: 540px;
            &-btn {
                &--cancel, &--add {
                    width: 540px;
                    height: 55px;
                    margin: 10px 10px;
                }
            }
        }

        .date-select {
            &--month {
                width: 408px;
                margin: 0px;
                margin-left: 20px;
            }
        }
    }

    @media (min-width: 480px) and (max-width: 767px){
        .add-date {
            min-width: 308px;
            &-btn {
                &--cancel, &--add {
                    width: 375px;
                    height: 55px;
                    margin: 10px 10px;
                }
            }
        }

        .date-select {
            &--month {
                width: 308px;
                margin: 0px;
                margin-left: 20px;
            }
        }
    }
    .image {
        height: 173px;
        width: 152px;
        left: 194px;
        top: 0px;
        border-radius: 0px;
        align-self: center;
    }
</style>
